<template>
  <s-table>
    <template #header>
      <tr>
        <th v-for="column in visibleColumns" :key="column.key" v-bind="column.th" v-on="column.th">
          <slot :name="`th:${column.key}`" v-bind="{toggleSort: () => setSortBy(column.key)}">
            <button
              v-if="column.sortable"
              @click="setSortBy(column.key)"
              class="link-subtle flex items-center gap-1"
            >
              {{ column.label }}
              <table-sorting-icon
                :column="column.key"
                :order-by="sortBy?.key"
                :direction="sortBy?.direction"
              />
            </button>
            <span v-else>
              {{ column.label }}
            </span>
          </slot>
        </th>
      </tr>
    </template>
    <template #body>
      <tr v-for="item in data" :key="item[keyBy]">
        <td
          v-for="column in visibleColumns"
          :key="column.key"
          v-bind="{...td, ...column.td}"
          v-on="{...td, ...column.td}"
          :data-id="item[keyBy]"
        >
          <slot :name="`td:${column.key}`" v-bind="item">
            {{ dig(item, column.key) }}
          </slot>
        </td>
      </tr>
      <tr v-if="!data.length">
        <td :colspan="visibleColumns.length" class="!text-center text-gray-600">
          <slot name="empty"></slot>
        </td>
      </tr>
    </template>
  </s-table>
</template>
<script setup lang="ts">
import STable from './STable.vue';
import TableSortingIcon from './TableSortingIcon.vue';
import {computed} from 'vue';
import STooltip from './STooltip.vue';

export type Column = {
  key: string;
  label: string;
  sortable?: boolean;
  th?: Record<string, any>;
  td?: Record<string, any>;
  hidden?: boolean;
};

export type SortBy = {
  key?: string;
  direction?: 'ASC' | 'DESC';
};

const {keyBy = 'id', columns} = defineProps<{
  columns: Column[];
  data: Record<string, any>[];
  keyBy?: string;
  td?: Record<string, any>;
}>();

const sortBy = defineModel<SortBy>('sortBy');

const setSortBy = (key: string = '') => {
  let direction = sortBy.value?.direction;

  if (sortBy.value?.key === key) {
    direction = sortBy.value?.direction === 'ASC' ? 'DESC' : 'ASC';
  }

  sortBy.value = {
    key,
    direction: direction || 'ASC',
  };
};

const visibleColumns = computed(() => {
  return columns.filter((column) => !column.hidden);
});

function dig(obj: any, path: string) {
  return path.split('.').reduce((o, i) => (o ? o[i] : undefined), obj);
}
</script>
